import React from "react";
import { DocMeta } from "../../docs/loader";

export type MediaRendererProps = {
  src: string;
  alt: string;
  title?: string;
  meta?: DocMeta;
};

function isYouTube(url: string): { id: string } | null {
  if (!url) return null;
  const match = url.match(/[?&]v=([\w-]{11})|youtu\.be\/(^[\s]+)?([\w-]{11})/);
  if (match) {
    const id = match[1] || match[3];
    if (id) return { id };
  }
  return null;
}

const MediaRenderer: React.FC<MediaRendererProps> = ({
  src,
  alt,
  title,
  meta,
}) => {
  // Frontmatter embeds
  if (meta?.embeds && meta.embeds.length > 0) {
    // Render only the first embed above inline media to avoid duplicates
    const yt = meta.embeds.find((e) => e.type === "youtube");
    if (yt) {
      return (
        <div className="media-responsive aspect-16-9">
          <iframe
            src={`https://www.youtube.com/embed/${yt.id}`}
            title={yt.title || alt}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }
  }

  // Title hint: "youtube:ID|caption: text"
  if (title && title.startsWith("youtube:")) {
    const parts = title.split("|");
    const id = parts[0].replace("youtube:", "");
    const caption = parts[1]?.replace("caption:", "").trim();
    return (
      <figure>
        <div className="media-responsive aspect-16-9">
          <iframe
            src={`https://www.youtube.com/embed/${id}`}
            title={caption || alt}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {caption && (
          <figcaption className="text-sm text-gray-500 mt-2">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  // Inline link auto-embed if looks like youtube
  const yt = isYouTube(src);
  if (yt) {
    return (
      <div className="media-responsive aspect-16-9">
        <iframe
          src={`https://www.youtube.com/embed/${yt.id}`}
          title={title || alt}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  if (src.endsWith(".mp4")) {
    return (
      <figure>
        <div className="media-responsive">
          <video src={src} controls playsInline />
        </div>
        {title && title.startsWith("caption:") && (
          <figcaption className="text-sm text-gray-500 mt-2">
            {title.replace("caption:", "").trim()}
          </figcaption>
        )}
      </figure>
    );
  }

  if (src.match(/\.(gif|png|jpe?g|webp)$/i)) {
    const caption = title?.startsWith("caption:")
      ? title.replace("caption:", "").trim()
      : undefined;
    return (
      <figure>
        <img src={src} alt={alt} />
        {caption && (
          <figcaption className="text-sm text-gray-500 mt-2">
            {caption}
          </figcaption>
        )}
      </figure>
    );
  }

  return <img src={src} alt={alt} title={title} />;
};

export default MediaRenderer;
